import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { ref, type Ref } from "vue";

/**
 * Detail state for a drill-down modal that fetches on every open. `load` clears the previous
 * detail and only the latest request may write `detail`, report its error or clear `isLoading`,
 * so a slow response for an earlier selection cannot overwrite the current one.
 */
export function useLatestDetail<T>() {
  const { globalErrorHandler } = useGlobalHandlers();
  const detail = ref(null) as Ref<T | null>;
  const isLoading = ref(false);
  let requestSeq = 0;

  const load = async (fetchDetail: () => Promise<{ data: T }>) => {
    const request = ++requestSeq;
    detail.value = null;
    isLoading.value = true;
    try {
      const response = await fetchDetail();
      if (request !== requestSeq) return;
      detail.value = response.data;
    } catch (error) {
      if (request === requestSeq) globalErrorHandler(error);
    } finally {
      if (request === requestSeq) isLoading.value = false;
    }
  };

  return { detail, isLoading, load };
}
